<template>
  <v-container fluid>
    <v-row justify="end">
      <v-col cols="12" align="center" justify="center">
        <v-sheet>
          <v-row>
            <v-col cols="9">
              <h1>Onboarding</h1>
            </v-col>
            <v-col cols="3">
              <v-select v-model="role" :items="roleOptions" label="Get my clients for role"> </v-select>
            </v-col>
          </v-row>
          <v-data-table
            :headers="table.headers"
            :options="table.options"
            :items="clients"
            :search="search"
            :show-group-by="true"
            :hide-default-footer="true"
            fixed-header
            height="750"
            disable-pagination
            dense
            :loading="loading"
          >
            <template v-slot:top>
              <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
                <template v-slot:label>
                  <v-icon color="secondary"> mdi-magnify </v-icon>
                  Search
                </template>
              </v-text-field>
            </template>
            <template v-slot:[`item.controls`]="props" width="100">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    elevation="13"
                    color="secondary"
                    class="ma-1 accent lighten-5"
                    dark
                    v-bind="attrs"
                    v-on="on"
                    @click="openNotes(props.item.client.id)"
                  >
                    <v-icon>mdi-notebook</v-icon>
                  </v-btn>
                </template>
                <span>Notes</span>
              </v-tooltip>
              <v-btn
                icon
                elevation="13"
                color="secondary"
                class="ma-1 accent lighten-5"
                dark
                @click="openCalendar(props.item.userId)"
              >
                <v-icon>mdi-calendar</v-icon>
              </v-btn>
            </template>

            <template v-slot:[`item.clientFullName`]="props">
              <div :class="getAgeingColor(props.item) + ' pa-1'">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <a
                      :href="props.item.profileLink"
                      target="_blank"
                      class="secondary--text text-body-1 text-decoration-none"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ props.item.clientFullName }}
                      <br />
                      Phone: {{ props.item.phone }}
                    </a>
                  </template>
                  <span>Last Login: {{ props.item.client.lastLogin | moment("YYYY-MM-DD HH:MM") }}</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:[`item.product`]="props">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ props.item.product }}
                  </span>
                </template>
                <span v-for="email in props.item.emailComms" :key="email.id" class="d-block">
                  {{ email.sgTemplateName }}: {{ email.event }}
                </span>
              </v-tooltip>
            </template>

            <template v-slot:[`item.connectFullName`]="props">
              <v-autocomplete
                v-if="editCell == 'connect-' + props.item.id"
                :value="props.item.connect && props.item.connect.id"
                dense
                :items="staff"
                @change="updateConnectUser($event, props.item.id)"
              ></v-autocomplete>
              <span v-else @click="editCell = 'connect-' + props.item.id" class="fake-link">
                {{ props.item.connectFullName }}
              </span>
            </template>
            <template v-slot:[`item.supportFullName`]="props">
              <v-autocomplete
                v-if="editCell == 'support-' + props.item.id"
                :value="props.item.support && props.item.support.id"
                dense
                :items="staff"
                @change="updateSupportUser($event, props.item.id)"
              ></v-autocomplete>
              <span v-else @click="editCell = 'support-' + props.item.id" class="fake-link">
                {{ props.item.supportFullName }}
              </span>
            </template>
            <template v-slot:[`item.coachFullName`]="props">
              <v-autocomplete
                v-if="editCell == 'coach-' + props.item.id"
                :value="props.item.coach && props.item.coach.id"
                dense
                :items="staff"
                @change="updateCoachUser($event, props.item.id)"
              ></v-autocomplete>
              <span v-else @click="editCell = 'coach-' + props.item.id" class="fake-link">
                {{ props.item.coachFullName }}
              </span>
            </template>
            <template v-slot:[`item.messageSentByUser`]="props">
              <v-icon v-if="props.item.messageSentByUser" color="complete-green">check_circle</v-icon>
              <v-icon v-else color="non-compliant">mdi-close</v-icon>
            </template>
            <template v-slot:[`item.messageSentByCoach`]="props">
              <v-icon v-if="props.item.messageSentByCoach" color="complete-green">check_circle</v-icon>
              <v-icon v-else color="non-compliant">mdi-close</v-icon>
            </template>
            <template v-slot:[`item.submittedMedicalForm`]="props">
              <v-icon v-if="props.item.submittedMedicalForm" color="complete-green">check_circle</v-icon>
              <v-icon v-else color="non-compliant">mdi-close</v-icon>
            </template>
            <template v-slot:[`item.initialAssessmentCompleted`]="props">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    Booked:
                    <v-icon v-if="props.item.initialAssessmentBooked != null" small color="complete-green"
                      >check_circle</v-icon
                    >
                    <v-icon v-else small color="non-compliant">mdi-close</v-icon>
                  </span>
                </template>
                {{ props.item.initialAssessmentBooked }}
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <a
                    v-if="props.item.initialAssessment && props.item.initialAssessment.id"
                    :href="`${$appApiEndPoint}/admin/dashboard/assessment/${props.item.initialAssessment.id}/change/`"
                    target="_blank"
                    >{{ props.item.initialAssessment.id }}</a
                  >
                  <span v-bind="attrs" v-on="on">
                    Submitted:
                    <v-icon v-if="props.item.initialAssessmentCompleted != null" small color="complete-green"
                      >check_circle</v-icon
                    >
                    <v-icon v-else small color="non-compliant">mdi-close</v-icon>
                  </span>
                </template>
                {{ props.item.initialAssessmentCompleted }}
              </v-tooltip>

              <v-tooltip v-if="props.item.initialAssessmentCompleted != null" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    Complete:
                    <v-icon v-if="assessessmentComplete(props.item.initialAssessment)" small color="complete-green"
                      >check_circle</v-icon
                    >
                    <v-icon v-else small color="non-compliant">mdi-close</v-icon>
                  </span>
                </template>
                Weight :{{ props.item.initialAssessment.weight }}
                <br />
                abdomen: {{ props.item.initialAssessment.abdomen }}
                <br />
                Chest {{ props.item.initialAssessment.chest }}
                <br />
                Hip: {{ props.item.initialAssessment.hip }}
                <br />
                Quad: {{ props.item.initialAssessment.quad }}
                <br />
                Img Front: {{ props.item.initialAssessment.imgFront }}
                <br />
                Img Back: {{ props.item.initialAssessment.imgBack }}
                <br />
                Img Side: {{ props.item.initialAssessment.imgSide }}
              </v-tooltip>
            </template>

            <template v-slot:[`item.program`]="props">
              <span><b>Program:</b> {{ props.item.program }}</span>
              <hr />
              <span><b>Nutrition Plan:</b> {{ props.item.nutritionPlan }}</span>
            </template>

            <template v-slot:[`item.onboardingCompleted`]="props">
              <v-simple-checkbox
                :ripple="false"
                color="secondary"
                class="mt-0"
                :value="props.item.onboardingCompleted"
                :disabled="loading || !onboardingComplete(props.item)"
                @input="updateOnboardingCompleted($event, props.item)"
              />
            </template>

            <template v-slot:[`item.coachingChatId`]="props">
              <coaching-chat-button
                :coaching-chat-id="props.item.coachingChatId"
                :user-id="props.item.client ? props.item.client.id : null"
                :coach-id="props.item.coach ? props.item.coach.id : null"
              />
            </template>
          </v-data-table>
          <v-dialog v-model="modals.notes" class="limited-height" width="900" transition="dialog-bottom-transition">
            <user-notes
              v-if="modals.notes"
              :program="selectedProgram"
              :userId="selectedUserId"
              :coachId="currentUser && currentUser.id"
              @problem="emitProblem()"
              @close="closeNotes"
            />
          </v-dialog>
          <v-dialog v-model="modals.calendar" class="limited-height" width="900" transition="dialog-bottom-transition">
            <v-card>
              <calendar v-if="modals.calendar" :userId="selectedUserId"></calendar>
            </v-card>
          </v-dialog>
        </v-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ONBOARDING_QUERY } from "@/constants/graphql/operations/onboarding";
import { UPDATE_SUBSCRIPTION_MUTATION } from "@/constants/graphql/users/subscription";
import { STAFF_QUERY } from "@/constants/graphql/users/user";
import { getUserName } from "@/lib/username";
import { fancy_date } from "@/lib/fancy_date";
import UserNotes from "@/components/dashboard/components/notes/UserNotes";
import Calendar from "@/components/dashboard/components/Calendar";
import CoachingChatButton from "@/components/chats/CoachingChatButton";

export default {
  name: "OperationsOnboarding",
  components: {
    UserNotes,
    Calendar,
    CoachingChatButton,
  },
  data() {
    return {
      clients: [],
      search: "",
      role: null,
      roleOptions: [
        { value: null, text: "" },
        { value: "coach", text: "Coach" },
        { value: "support", text: "Support Owner" },
        { value: "connect", text: "Onboarding Owner" },
      ],
      editCell: null,
      staff: [],
      loading: true,
      menu: [],
      selectedProgram: null,
      selectedUserId: null,
      modals: {
        notes: false,
        calendar: false,
      },
      table: {
        options: {
          showGroupBy: true,
          multiSort: false,
        },
        headers: [
          { text: "Sign up date", value: "signUpDate", groupable: false },
          { text: "Client", value: "clientFullName", groupable: false },
          { text: "Product", value: "product", groupable: false, width: 40 },
          { text: "Package", value: "package", groupable: false, width: 40 },
          {
            text: "Onboarding owner",
            value: "connectFullName",
            groupable: false,
          },
          {
            text: "",
            value: "controls",
            sortable: false,
            groupable: false,
            filterable: false,
            width: 20,
          },
          { text: "Support team", value: "supportFullName", groupable: false },
          { text: "Coach", value: "coachFullName", groupable: false },
          {
            text: "Message sent by client",
            value: "messageSentByUser",
            groupable: false,
          },
          {
            text: "Message sent by coach",
            value: "messageSentByCoach",
            groupable: false,
          },
          {
            text: "Completed Medical Form",
            value: "submittedMedicalForm",
            groupable: false,
            width: 20,
          },
          {
            text: "Initial Assessment",
            value: "initialAssessmentCompleted",
            groupable: false,
          },
          {
            text: "Program Start Date",
            value: "programStartDate",
            groupable: false,
          },
          { text: "Legacy Program", value: "program", groupable: false },
          { text: "Workout Block", value: "hasWorkoutBlock", groupable: false },
          { text: "Has Nutrition Plan", value: "hasNutritionPlan", groupable: false },
          { text: "Has course Block", value: "hasCourseBlock", groupable: false },
          { text: "Coaching Chat", value: "coachingChatId", groupable: false },
          {
            text: "Complete onboarding",
            value: "onboardingCompleted",
            groupable: false,
          },
        ],
      },
    };
  },
  computed: {
    currentUser() {
      return JSON.parse(localStorage.getItem("user")) || store.getters.user;
    },
  },
  methods: {
    getAgeingColor(item) {
      var diff = this.$moment().diff(this.$moment(item.signUpDate), "days");

      if (diff > 7) {
        return "metric-red";
      }
      if (diff > 3 && diff < 8) {
        return "metric-yellow";
      }
      return "metric-green";
    },
    fancyDate(date) {
      return fancy_date(date);
    },
    getName(user) {
      getUserName(user);
    },
    assessessmentComplete(initialAssessment) {
      return (
        initialAssessment?.weight &&
        initialAssessment?.abdomen &&
        initialAssessment?.chest &&
        initialAssessment?.hip &&
        initialAssessment?.quad
      );
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    updateConnectUser(event, userSubscriptionId) {
      this.mutateSubscription({
        id: userSubscriptionId,
        connectUserId: event,
      });
    },
    updateSupportUser(event, userSubscriptionId) {
      this.mutateSubscription({
        id: userSubscriptionId,
        supportUserId: event,
      });
    },
    updateCoachUser(event, userSubscriptionId) {
      this.mutateSubscription({
        id: userSubscriptionId,
        coachUserId: event,
      });
    },
    updateOnboardingCompleted(event, item) {
      let userSubscriptionId = item.id;
      this.loading = true;
      item.onboardingCompleted = true;
      this.mutateSubscription({
        id: userSubscriptionId,
        onboardingCompleted: event,
      });
    },
    mutateSubscription(input) {
      this.$apollo
        .mutate({
          mutation: UPDATE_SUBSCRIPTION_MUTATION,
          variables: {
            input: input,
          },
          refetchQueries: ["userSubscriptions"],
        })
        .then((result) => {
          if (!result.data.updateSubscriptionRecord.updated) {
            alert(result.data.updateSubscriptionRecord.error);
          } else {
            this.$toasted.success("Updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
    openNotes(userId) {
      this.selectedUserId = userId;
      this.modals.notes = true;
    },
    closeNotes() {
      this.selectedUserId = null;
      this.modals.notes = false;
    },
    openCalendar(userId) {
      this.selectedUserId = userId;
      this.modals.calendar = true;
    },
    closeCalendar() {
      this.selectedUserId = null;
      this.modals.calendar = false;
    },
    onboardingComplete(item) {
      return (
        item.initialAssessment != null &&
        this.assessessmentComplete(item.initialAssessment) &&
        item.programStartDate &&
        item.nutritionPlan
      );
    },
  },
  apollo: {
    userSubscriptions: {
      query: ONBOARDING_QUERY,
      variables() {
        this.loading = true;
        let input = {
          stage: "onboarding",
          productType: "one-on-one",
        };
        if (this.$route.query.role || this.role) {
          let role = this.role || this.$route.query.role;
          this.role = role;
          let userId = this.$route.query.userId || this.currentUser.id;
          let encoded_json_payload = btoa("UserNode:" + userId);

          switch (role) {
            case "coach":
              input["coachId"] = encoded_json_payload;
              break;
            case "support":
              input["supportOwnerId"] = encoded_json_payload;
              break;
            case "connect":
              input["connectOwnerId"] = encoded_json_payload;
              break;
          }
        }
        return input;
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.clients = [];

          this.clients = response.data.userSubscriptions.edges.map((item) => {
            return {
              id: this.getId(item.node.id),
              client: item.node.user,
              clientFullName: getUserName(item.node.user),
              coach: item.node.coach,
              connect: item.node.connectOwner,
              support: item.node.supportOwner,
              connectFullName: item.node.connectOwner ? getUserName(item.node.connectOwner) : "Add",
              supportFullName: item.node.supportOwner ? getUserName(item.node.supportOwner) : "Add",
              coachFullName: item.node.coach ? getUserName(item.node.coach) : "Add",
              profileLink: `/clients/${item.node.user.id}`,
              signUpDate: this.$moment(item.node.startDate).format("YYYY-MM-DD"),
              programStartDate: item.node.programStartDate,
              submittedMedicalForm: item.node.submittedMedicalForm,
              onboardingCompleted: item.node.onboardingCompleted,
              phone: item.node.user.userProfile.phone,
              product: item.node.product.name,
              messageSentByUser: item.node.messageSentByUser,
              messageSentByCoach: item.node.messageSentByCoach,
              onboardingProgram: item.node.onboardingProgram,
              nutritionPlan: item.node.user.userProfile.coachingNutritionPlan
                ? `${item.node.user.userProfile.coachingNutritionPlan.servingSize} - ${item.node.user.userProfile.coachingNutritionPlan.dietaryRestriction}`
                : "Not set",
              program:
                item.node.onboardingProgram && item.node.onboardingProgram.nextProgram
                  ? item.node.onboardingProgram.nextProgram.name
                  : "not set",
              initialAssessment: item.node.initialAssessment,
              hasNutritionPlan: item.node.user.userProfile.coachingNutritionPlan ? "Yes" : "No",
              hasWorkoutBlock:
                item.node.user.subscriptionblockSet.filter(
                  (block) =>
                    this.$moment(item.node.startDate) < this.$moment(block.startDate) &&
                    (block.block.type == "PROGRAM" || block.block.type == "CHALLENGE")
                ).length > 0
                  ? "Yes"
                  : "No",
              hasCourseBlock:
                item.node.user.subscriptionblockSet.filter(
                  (block) =>
                    this.$moment(item.node.startDate) < this.$moment(block.startDate) && block.block.type == "COURSE"
                ).length > 0
                  ? "Yes"
                  : "No",
              emailComms: item.node.userEmailComms.filter(
                (log) => this.$moment(log.createdAt) >= this.$moment(item.node.startDate)
              ),
              initialAssessmentBooked:
                item.node.initialAssessment && item.node.initialAssessment.requiredDate
                  ? this.$moment(item.node.initialAssessment.requiredDate).format("YYYY-MM-DD HH:mm")
                  : null,
              initialAssessmentCompleted:
                item.node.initialAssessment && item.node.initialAssessment.assessmentDate
                  ? this.$moment(item.node.initialAssessment.assessmentDate).format("YYYY-MM-DD HH:mm")
                  : null,
              package: item.node.billingOrder?.package?.title,
              coachingChatId: item.node.user?.userProfile?.coachingChat?.id,
            };
          });

          this.loading = false;
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
    users: {
      query: STAFF_QUERY,
      variables() {
        return {
          isActive: true,
          isStaff: true,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.staff = [];

          response.data.users.edges.forEach((item) => {
            let data = {
              value: this.getId(item.node.id),
              text: getUserName(item.node),
            };

            this.staff.push(data);
          });

          this.staff.sort((a, b) => (a.text > b.text ? 1 : -1));
        }
        if (response.error && response.error.gqlError?.message == "Permission Denied.") {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>

<style scoped>
.fake-link {
  cursor: pointer;
}
</style>
